import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import {
  Divider,
  Stack,
  ToggleButton,
  useTheme,
  Tooltip,
  Card,
  Container,
  MenuItem,
  TextField,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Computer,
  FormatAlignCenter,
  FormatAlignJustify,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatUnderlined,
  Smartphone,
  TabletMac,
} from "@mui/icons-material";
import { AUTURA_ASPHALT_DARK, AUTURA_ASPHALT_LIGHT } from "utils";
import { useTranslation } from "react-i18next";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $createParagraphNode,
  $isRangeSelection,
  $isElementNode,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  ElementNode,
} from "lexical";
import { $setBlocksType, $wrapNodes } from "@lexical/selection";
import {
  $createHeadingNode,
  HeadingTagType,
  $isHeadingNode,
} from "@lexical/rich-text";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
} from "@lexical/list";
import { toggleButtonGroupStyles } from "./styles";

const LOW_PRIORITY = 1;

type TextFormat = "bold" | "italic" | "underline";
type Alignment = "left" | "center" | "right" | "justify" | null;
type ListType = "ordered" | "unordered" | null;
type FontStyle = "title" | "heading" | "paragraph" | "fine-print";
export type PreviewMode = "xs" | "sm" | "md";

export interface EditorToolbarProps {
  previewMode: PreviewMode;
  setPreviewMode: (newPreviewMode: PreviewMode) => void;
}

export function EditorToolbar({
  previewMode,
  setPreviewMode,
}: EditorToolbarProps): ReactNode {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation();
  const { palette } = useTheme();
  const [alignment, setAlignment] = useState<Alignment>("left");
  const [format, setFormat] = useState<TextFormat[]>([]);
  const [listType, setListType] = useState<ListType>(null);
  const [fontStyle, setFontStyle] = useState<FontStyle>("paragraph");

  const handleAlignment = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: "left" | "center" | "right" | "justify" | null,
  ) => {
    if (!newAlignment) {
      return;
    }
    setAlignment(newAlignment);
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, newAlignment);
    editor.focus();
  };
  const handleFormat = (
    event: React.MouseEvent<HTMLElement>,
    newFormat: TextFormat[],
  ) => {
    const oldFormat = [...format];
    const changedFormats = [...oldFormat, ...newFormat].filter(
      (f) => !oldFormat.includes(f) || !newFormat.includes(f),
    );

    changedFormats.forEach((f) => {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, f);
    });

    setFormat(newFormat);
    editor.focus();
  };
  const handleListTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newListType: "ordered" | "unordered" | null,
  ) => {
    setListType(newListType);
    if (!newListType) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else if (newListType === "ordered") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else if (newListType === "unordered") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
    editor.focus();
  };
  const handleFontStyleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as FontStyle;
    setFontStyle(value);
    let headingType: HeadingTagType;
    switch (value) {
      case "title":
        headingType = "h1";
        break;
      case "heading":
        headingType = "h2";
        break;
      case "fine-print":
        headingType = "h3";
        break;
      default:
        headingType = "h4";
        break;
    }
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (headingType === "h4") {
          $wrapNodes(selection, () => $createParagraphNode());
        } else {
          $setBlocksType(selection, () => $createHeadingNode(headingType));
        }
      }
    });
    editor.focus();
  };
  const handlePreviewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newPreviewMode: PreviewMode,
  ) => {
    setPreviewMode(newPreviewMode);
  };

  const updateToolbar = () => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      // Alignment
      if ($isElementNode(element)) {
        const alignment = element.getFormatType();
        if (alignment === "") {
          setAlignment("left");
        } else if (
          alignment &&
          ["left", "center", "right", "justify"].includes(alignment)
        ) {
          setAlignment(alignment as "left" | "center" | "right" | "justify");
        }
      }

      // Font Style
      if ($isHeadingNode(element)) {
        const tag = element.getTag();
        if (tag === "h1") {
          setFontStyle("title");
        } else if (tag === "h2") {
          setFontStyle("heading");
        } else if (tag === "h3") {
          setFontStyle("fine-print");
        } else {
          setFontStyle("paragraph");
        }
      } else {
        setFontStyle("paragraph");
      }

      // List Type
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          if (type === "ul") {
            setListType("unordered");
          } else if (type === "ol") {
            setListType("ordered");
          }
        } else {
          setListType(null);
        }
      }

      // Text Format
      const newFormat: TextFormat[] = [];
      const hasBold = selection.hasFormat("bold");
      const hasItalic = selection.hasFormat("italic");
      const hasUnderline = selection.hasFormat("underline");
      if (hasBold) {
        newFormat.push("bold");
      }
      if (hasItalic) {
        newFormat.push("italic");
      }
      if (hasUnderline) {
        newFormat.push("underline");
      }
      setFormat(newFormat);
    }
  };

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar();
          return false;
        },
        LOW_PRIORITY,
      ),
    );
  }, [editor, updateToolbar]);

  return (
    <Container maxWidth="md" sx={{ pb: 2, position: "relative" }}>
      <Card
        sx={{
          borderRadius: 1,
          backgroundColor: AUTURA_ASPHALT_LIGHT,
        }}
        elevation={3}
        raised
      >
        <Stack direction={"row"} display={"flex"} px={2}>
          <FontStyleSelect
            fontStyle={fontStyle}
            handleFontStyleChange={handleFontStyleChange}
          />
          <Divider flexItem orientation="vertical" variant="middle" />
          <ToggleButtonGroup
            value={format}
            onChange={handleFormat}
            aria-label="text formatting"
            sx={{
              ...toggleButtonGroupStyles,
              backgroundColor: AUTURA_ASPHALT_LIGHT,
            }}
          >
            <Tooltip title={t("editor.bold")} arrow>
              <ToggleButton value="bold" aria-label="format bold">
                <FormatBold
                  htmlColor={
                    format.includes("bold")
                      ? palette.primary.main
                      : AUTURA_ASPHALT_DARK
                  }
                />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t("editor.italic")} arrow>
              <ToggleButton value="italic" aria-label="format italic">
                <FormatItalic
                  htmlColor={
                    format.includes("italic")
                      ? palette.primary.main
                      : AUTURA_ASPHALT_DARK
                  }
                />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t("editor.underline")} arrow>
              <ToggleButton value="underline" aria-label="format underline">
                <FormatUnderlined
                  htmlColor={
                    format.includes("underline")
                      ? palette.primary.main
                      : AUTURA_ASPHALT_DARK
                  }
                />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
          <Divider flexItem orientation="vertical" variant="middle" />
          <ToggleButtonGroup
            value={alignment}
            exclusive
            onChange={handleAlignment}
            aria-label="text alignment"
            sx={{
              ...toggleButtonGroupStyles,
              backgroundColor: AUTURA_ASPHALT_LIGHT,
            }}
          >
            <Tooltip title={t("editor.alignLeft")} arrow>
              <ToggleButton value="left" aria-label="left aligned">
                <FormatAlignLeft
                  htmlColor={
                    alignment === "left"
                      ? palette.primary.main
                      : AUTURA_ASPHALT_DARK
                  }
                />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t("editor.alignCenter")} arrow>
              <ToggleButton value="center" aria-label="centered">
                <FormatAlignCenter
                  htmlColor={
                    alignment === "center"
                      ? palette.primary.main
                      : AUTURA_ASPHALT_DARK
                  }
                />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t("editor.alignRight")} arrow>
              <ToggleButton value="right" aria-label="right aligned">
                <FormatAlignRight
                  htmlColor={
                    alignment === "right"
                      ? palette.primary.main
                      : AUTURA_ASPHALT_DARK
                  }
                />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t("editor.alignJustify")} arrow>
              <ToggleButton value="justify" aria-label="justified">
                <FormatAlignJustify
                  htmlColor={
                    alignment === "justify"
                      ? palette.primary.main
                      : AUTURA_ASPHALT_DARK
                  }
                />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
          <Divider flexItem orientation="vertical" variant="middle" />
          <ToggleButtonGroup
            value={listType}
            exclusive
            onChange={handleListTypeChange}
            aria-label="list formatting"
            sx={{
              ...toggleButtonGroupStyles,
              backgroundColor: AUTURA_ASPHALT_LIGHT,
            }}
          >
            <Tooltip title={t("editor.ol")} arrow>
              <ToggleButton value="ordered" aria-label="ordered list">
                <FormatListNumbered />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t("editor.ul")} arrow>
              <ToggleButton value="unordered" aria-label="unordered list">
                <FormatListBulleted />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
          <Divider flexItem orientation="vertical" variant="middle" />
          <ToggleButtonGroup
            value={previewMode}
            exclusive
            onChange={handlePreviewModeChange}
            aria-label="preview mode"
            sx={{
              ...toggleButtonGroupStyles,
              backgroundColor: AUTURA_ASPHALT_LIGHT,
            }}
          >
            <Tooltip title={t("editor.xs_preview")} arrow>
              <ToggleButton value="xs">
                <Smartphone
                  htmlColor={
                    previewMode === "xs"
                      ? palette.primary.main
                      : AUTURA_ASPHALT_DARK
                  }
                />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t("editor.sm_preview")} arrow>
              <ToggleButton value="sm">
                <TabletMac
                  htmlColor={
                    previewMode === "sm"
                      ? palette.primary.main
                      : AUTURA_ASPHALT_DARK
                  }
                />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t("editor.md_preview")} arrow>
              <ToggleButton value="md">
                <Computer
                  htmlColor={
                    previewMode === "md"
                      ? palette.primary.main
                      : AUTURA_ASPHALT_DARK
                  }
                />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Stack>
      </Card>
    </Container>
  );
}

export interface FontStyleSelectProps {
  fontStyle: FontStyle;
  handleFontStyleChange: (e: ChangeEvent<HTMLInputElement>) => void;
}
export const FontStyleSelect = ({
  fontStyle,
  handleFontStyleChange,
}: FontStyleSelectProps) => {
  const { t } = useTranslation();
  return (
    <TextField
      sx={{
        backgroundColor: AUTURA_ASPHALT_LIGHT,
        px: 2,
      }}
      margin={"dense"}
      variant={"standard"}
      fullWidth
      id={"font-style-select"}
      name={"fontStyle"}
      value={fontStyle}
      onChange={handleFontStyleChange}
      SelectProps={{
        disableUnderline: true,
        sx: {
          pt: 1,
          backgroundColor: AUTURA_ASPHALT_LIGHT,
        },
      }}
      size={"small"}
      select
    >
      <MenuItem value="title" divider sx={{ height: 47 }}>
        <span className={"editor-title"}>{t("editor.textStyle_title")}</span>
      </MenuItem>
      <MenuItem value="heading" divider sx={{ height: 47 }}>
        <span className={"editor-heading"}>
          {t("editor.textStyle_heading")}
        </span>
      </MenuItem>
      <MenuItem value="paragraph" divider sx={{ height: 47 }}>
        <span className={"editor-paragraph"}>
          {t("editor.textStyle_paragraph")}
        </span>
      </MenuItem>
      <MenuItem value="fine-print" sx={{ height: 47 }}>
        <span className={"editor-caption"}>
          {t("editor.textStyle_finePrint")}
        </span>
      </MenuItem>
    </TextField>
  );
};

export interface LinkEditorProps {
  onCancel: () => void;
  onApply: (newLinkUrl: string) => void;
  anchor: ElementNode | null;
}
